<template>
  <div class="min-w-0">
    <span class="text-black font-semibold flex">FIELD SELECTION (Qdrant)</span>
    <p class="text-gray-dark text-sm mt-1">
      Pick which feed fields to <strong>embed</strong> (semantic search), which to keep in the
      <strong>payload</strong> (filters / card display), and the <strong>group key</strong> that lets
      size/variant rows reuse one embedding.
    </p>
    <p class="text-gray-dark text-sm mt-1">{{ indexingHint }}</p>

    <div class="mt-3 max-h-80 overflow-y-auto pr-1">
      <div v-for="tag in availableTags" :key="tag" class="border-solid border-0 border-b border-gray-border py-2">
        <div class="flex items-center justify-between gap-2">
          <span class="font-mono text-sm truncate" :title="tag">{{ tag }}</span>
          <div class="flex items-center gap-3 text-xs flex-shrink-0">
            <label class="flex items-center gap-1">
              <input type="checkbox" :checked="isEmbed(tag)" @change="toggleEmbed(tag, $event.target.checked)"/>Embed
            </label>
            <label class="flex items-center gap-1">
              <input type="checkbox" :checked="isPayload(tag)" @change="togglePayload(tag, $event.target.checked)"/>Payload
            </label>
            <label class="flex items-center gap-1">
              <input type="checkbox" :checked="isGroupKey(tag)" @change="toggleGroupKey(tag, $event.target.checked)"/>Group key
            </label>
          </div>
        </div>
        <div v-if="isPayload(tag)" class="flex gap-2 mt-2 pl-2">
          <input :value="payloadFields[tag].as" @input="setPayloadProp(tag, 'as', $event.target.value)"
                 class="border border-solid border-gray-border h-8 px-2 text-sm flex-1 min-w-0" placeholder="store as"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import without from 'ramda/src/without'
import UserProfileService from '@/services/amio/user-profile.service'

// derives a clean payload key from a feed tag (g:item_group_id -> item_group_id)
function defaultPayloadName(tag) {
  return tag.replace(/^[a-zA-Z]+:/, '').replace(/[^a-zA-Z0-9_]/g, '_')
}

// Per-source field selection for a Qdrant feed. Decides embed / payload(as) / group_key.
// NOTE: the payload field TYPE and whether it is *indexed* are NOT set here — both are
// collection-global in Qdrant and live in the collection registry (Settings → Vector Collections).
// Emits `input` with { embed_fields: [...], payload_fields: { tag: { as } }, group_key: [...] }.
export default {
  name: 'SettingsIndexFieldSelection',
  props: {
    availableTags: {
      type: Array,
      default: () => []
    },
    value: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    const value = this.value || {}
    return {
      embedFields: [...(value.embed_fields || [])],
      payloadFields: { ...(value.payload_fields || {}) },
      groupKey: [...(value.group_key || [])]
    }
  },
  computed: {
    // Indexing is collection-global and lives in the registry, which only admins can reach — so the
    // pointer to that page is shown to admins only; regular users are just told it's admin-managed.
    indexingHint() {
      return UserProfileService.isAdmin() ?
        'Which payload fields are indexed is set per collection in Settings → Vector Collections.' :
        'Which payload fields are indexed is managed at the collection level by an administrator.'
    },
    config() {
      return {
        embed_fields: this.embedFields,
        payload_fields: this.payloadFields,
        group_key: this.groupKey
      }
    }
  },
  watch: {
    config: {
      deep: true,
      handler(config) {
        this.$emit('input', config)
      }
    }
  },
  methods: {
    isEmbed(tag) {
      return this.embedFields.includes(tag)
    },
    isPayload(tag) {
      return Object.prototype.hasOwnProperty.call(this.payloadFields, tag)
    },
    isGroupKey(tag) {
      return this.groupKey.includes(tag)
    },
    toggleEmbed(tag, on) {
      this.embedFields = on ? [...this.embedFields, tag] : without([tag], this.embedFields)
    },
    toggleGroupKey(tag, on) {
      this.groupKey = on ? [...this.groupKey, tag] : without([tag], this.groupKey)
    },
    togglePayload(tag, on) {
      const payloadFields = { ...this.payloadFields }
      if (on) {
        payloadFields[tag] = { as: defaultPayloadName(tag) }
      } else {
        delete payloadFields[tag]
      }
      this.payloadFields = payloadFields
    },
    setPayloadProp(tag, prop, value) {
      this.payloadFields = {
        ...this.payloadFields,
        [tag]: { ...this.payloadFields[tag], [prop]: value }
      }
    }
  }
}
</script>
